
import { mapState, mapGetters, mapMutations } from 'vuex';
import AppFilterSectionProductOptionType from './AppFilterSection/AppFilterSectionProductOptionType';
import AppFilterSectionSearchBar from './AppFilterSection/AppFilterSectionSearchBar';
import AppFilterSectionSearchBarPc from './AppFilterSection/AppFilterSectionSearchBarPc';
import AppFilterSectionUserProperty from './AppFilterSection/AppFilterSectionUserProperty';
import AppFilterSectionReviewOptionTypeFilters from './AppFilterSection/AppFilterSectionReviewOptionTypeFilters';
import AppFilterSectionKeywordFilters from './AppFilterSection/AppFilterSectionKeywordFilters';
import AppFilterSectionCategoryFilter from './AppFilterSection/AppFilterSectionCategoryFilter';
import LocalStorageKeys from '@/constants/LOCAL_STORAGE_KEYS';

export default {
  name: 'AppFilterSection',
  components: {
    AppFilterSectionProductOptionType,
    AppFilterSectionSearchBar,
    AppFilterSectionSearchBarPc,
    AppFilterSectionUserProperty,
    AppFilterSectionReviewOptionTypeFilters,
    AppFilterSectionKeywordFilters,
    AppFilterSectionCategoryFilter
  },
  props: {
    params: { type: Object, default: () => ({}) }
  },
  data() {
    return {
      RATING_OPTIONS: [
        { name: this.$t('search_bar.score.5'), value: 5, disabled: false },
        { name: this.$t('search_bar.score.4'), value: 4, disabled: false },
        { name: this.$t('search_bar.score.3'), value: 3, disabled: false },
        { name: this.$t('search_bar.score.2'), value: 2, disabled: false },
        { name: this.$t('search_bar.score.1'), value: 1, disabled: false }
      ],
      dragActive: false,
      dragMoved: false,
      dragStartX: 0,
      dragScrollLeft: 0,
      dragTarget: null,
      selectedScores: [],
      weightOptionId: JSON.parse(
        localStorage.getItem(LocalStorageKeys.WEIGHT_OPTION_ID)
      ),
      heightOptionId: JSON.parse(
        localStorage.getItem(LocalStorageKeys.HEIGHT_OPTION_ID)
      ),
      selectedCategory: null,
      selectedProductOptionTypes: {},
      selectedReviewOptions: {},
      selectedReviewTagTypes: [],
      useFilterByVerifiedBuyer: false
    };
  },
  computed: {
    ...mapState('widget', ['widget']),
    ...mapGetters('widget', ['isMobileViewport']),
    ...mapState('reviewSettings', ['reviewSettings']),
    ...mapState('review', ['hasMedia']),
    ...mapState('userProperties', [
      'isUserPropertyFilterShowable',
      'userWeight',
      'userHeight'
    ]),
    ...mapState('reviewOptionTypes', ['isReviewOptionTypeFilterShowable']),
    ...mapGetters('widget', [
      'displayMediaReviewsFirstToggleListV3',
      'showScoreFilter',
      'showVerifiedBuyerFilter'
    ]),
    displayProductOptionTypeFilter() {
      return (
        this.widget.show_filter_section &&
        this.widget.show_product_option_filter
      );
    },
    displaySearchBarSection() {
      return (
        this.widget.show_sort_section ||
        this.reviewSettings.enable_review_search ||
        (this.displayMediaReviewsFirstToggleListV3 && this.hasMedia)
      );
    },
    displayKeywordFilter() {
      return (
        this.widget.show_filter_section && this.widget.use_review_keyword_filter
      );
    },
    displayCategoryFilter() {
      return (
        this.widget.show_filter_section &&
        this.widget.show_top_filter_menu &&
        !this.widget.category_id
      );
    },
    displayReviewOptionTypeFilters() {
      return (
        this.widget.show_filter_section && this.isReviewOptionTypeFilterShowable
      );
    },
    displayUserPropertyFilter() {
      return (
        this.widget.show_filter_section &&
        this.widget.show_size_filter &&
        this.isUserPropertyFilterShowable
      );
    },
    displayMainFilterSection() {
      return (
        this.displayUserPropertyFilter ||
        this.displayCategoryFilter ||
        this.showScoreFilter ||
        this.displayReviewOptionTypeFilters
      );
    },
    displayResetButton() {
      return (
        this.selectedCategory ||
        this.selectedScores.length ||
        this.selectedReviewOptionKeys.length > 0 ||
        this.selectedProductOptionKeys.length > 0 ||
        this.selectedReviewTagTypes.length > 0 ||
        this.userWeight ||
        this.userHeight
      );
    },
    selectedReviewOptionKeys() {
      return Object.keys(this.selectedReviewOptions);
    },
    selectedProductOptionKeys() {
      return Object.keys(this.selectedProductOptionTypes);
    },
    reviewOptionResetParams() {
      return this.selectedReviewOptionKeys.reduce(
        (accumulate, key) => ({ ...accumulate, [key]: null }),
        {}
      );
    },
    productOptionResetParams() {
      return this.selectedProductOptionKeys.reduce(
        (accumulate, key) => ({ ...accumulate, [key]: null }),
        {}
      );
    }
  },
  watch: {
    params() {
      this.syncSelectionsFromParams();
    }
  },
  created() {
    this.syncSelectionsFromParams();
  },
  methods: {
    ...mapMutations('userProperties', ['SET_USER_WEIGHT', 'SET_USER_HEIGHT']),
    syncSelectionsFromParams() {
      const {
        scores,
        category_id,
        filter_by_verified_buyer,
        user_weight,
        user_height
      } = this.params;
      this.selectedScores = scores
        ? scores.split(',').map(s => parseInt(s))
        : [];
      this.selectedCategory = category_id ? parseInt(category_id) : null;
      this.useFilterByVerifiedBuyer = filter_by_verified_buyer === 'true';
      this.SET_USER_WEIGHT(user_weight || null);
      this.SET_USER_HEIGHT(user_height || null);
      this.parseProductOptionTypes();
      this.parseReviewOptionTypes();
      this.parseReviewTagTypes();
    },
    onDragStart(e) {
      this.dragActive = true;
      this.dragMoved = false;
      this.dragStartX = e.clientX;
      this.dragTarget = e.currentTarget;
      this.dragScrollLeft = this.dragTarget.scrollLeft;
    },
    onDragMove(e) {
      if (!this.dragActive) return;

      const DRAG_THRESHOLD = 5;
      const dx = e.clientX - this.dragStartX;
      if (Math.abs(dx) > DRAG_THRESHOLD) this.dragMoved = true;
      this.dragTarget.scrollLeft = this.dragScrollLeft - dx;
    },
    onDragEnd() {
      this.dragActive = false;
      this.dragTarget = null;
      if (this.dragMoved) {
        requestAnimationFrame(() => {
          this.dragMoved = false;
        });
      }
    },
    submit(params) {
      this.$emit('submit', { ...params, page: '1' });
    },
    onChangeUserPropertyFilter({ user_weight, user_height }) {
      const newParams = { ...this.params, user_weight, user_height };
      const weightOptionKey = `option_${this.weightOptionId}`;
      const heightOptionKey = `option_${this.heightOptionId}`;

      if (
        (user_weight && this.weightOptionId) ||
        (user_height && this.heightOptionId)
      ) {
        this.$delete(this.selectedReviewOptions, weightOptionKey);
        this.$delete(this.selectedReviewOptions, heightOptionKey);
        newParams[weightOptionKey] = null;
        newParams[heightOptionKey] = null;
      }

      this.submit(newParams);
    },
    onChangeCategoryFilter(categoryId) {
      this.selectedCategory = categoryId;
      this.submit({ category_id: categoryId });
    },
    parseProductOptionTypes() {
      this.selectedProductOptionTypes = Object.entries(this.params)
        .filter(([key]) => key.startsWith('product_option_'))
        .reduce((options, [key, value]) => {
          const optionId = key.split('product_option_')[1];
          options[optionId] = value.split(',').map(v => parseInt(v));
          return options;
        }, {});
    },
    parseReviewOptionTypes() {
      this.selectedReviewOptions = Object.entries(this.params)
        .filter(([key]) => key.startsWith('option_'))
        .reduce((options, [key, value]) => {
          options[key] = value.split(',');
          return options;
        }, {});
    },
    parseReviewTagTypes() {
      const reviewTagTypeIds = this.params.review_tag_type_ids;
      this.selectedReviewTagTypes = reviewTagTypeIds
        ? reviewTagTypeIds.split(',').map(id => parseInt(id))
        : [];
    },
    onChangeProductOptionTypes(newOptions) {
      const newParams = {};
      Object.keys(this.selectedProductOptionTypes).forEach(id => {
        if (!(id in newOptions)) {
          newParams[`product_option_${id}`] = null;
        }
      });

      const filteredOptions = {};
      Object.entries(newOptions).forEach(([id, values]) => {
        filteredOptions[id] = values;
        newParams[`product_option_${id}`] = values.join(',');
      });

      this.selectedProductOptionTypes = filteredOptions;
      this.submit(newParams);
    },
    onChangeReviewOptions(id, value) {
      const newParams = { ...this.params };
      const weightOptionKey = `option_${this.weightOptionId}`;
      const heightOptionKey = `option_${this.heightOptionId}`;

      if (
        (id === weightOptionKey && value.length) ||
        (id === heightOptionKey && value.length)
      ) {
        newParams['user_weight'] = null;
        newParams['user_height'] = null;
        this.SET_USER_WEIGHT(null);
        this.SET_USER_HEIGHT(null);
      }

      if (value.length) this.$set(this.selectedReviewOptions, id, value);
      else this.$delete(this.selectedReviewOptions, id);

      this.submit({ ...newParams, [id]: value.join(',') });
    },
    onChangeReviewTagTypes(reviewTagTypes) {
      this.selectedReviewTagTypes = reviewTagTypes;
      this.submit({ review_tag_type_ids: reviewTagTypes.join(',') });
    },
    onChangeVerifiedBuyerFilter() {
      this.submit({ filter_by_verified_buyer: this.useFilterByVerifiedBuyer });
    },
    resetFilters() {
      this.submit({
        category_id: null,
        scores: [],
        review_tag_type_ids: [],
        user_weight: null,
        user_height: null,
        ...this.reviewOptionResetParams,
        ...this.productOptionResetParams
      });

      this.SET_USER_WEIGHT(null);
      this.SET_USER_HEIGHT(null);
      this.selectedCategory = null;
      this.selectedScores = [];
      this.selectedReviewTagTypes = [];
      this.selectedReviewOptions = {};
      this.selectedProductOptions = {};
    }
  }
};
